import React, { useEffect, useState } from 'react'
import throttle from 'lodash.throttle'

import { useForDesktop } from '@sketch/components'
import { useGetNotificationQuery } from '@sketch/gql-types'

import { InboxPreviewActions } from './InboxPreviewActions'
import { InboxPreviewBody } from './InboxPreviewBody'
import { InboxPreviewHeader } from './InboxPreviewHeader'
import { InboxPreviewReason } from './InboxPreviewReason'
import { InboxContent, Root, VerticalDivider } from './InboxPreview.styles'
import {
  getNotificationConfig,
  resolveNotificationTitle,
} from './notificationConfig'

interface InboxPreviewProps {
  notificationId: string
}

const SCROLLED_THRESHOLD_PX = 4
const SCROLL_THROTTLE_MS = 100

export const InboxPreview = ({ notificationId }: InboxPreviewProps) => {
  const isDesktop = useForDesktop()
  const { data, loading } = useGetNotificationQuery({
    errorPolicy: 'all',
    variables: { identifier: notificationId },
  })

  // Track whether Root (the preview's scroll container) has scrolled, so the
  // sticky actions bar can fade in its backdrop. A callback ref (rather than
  // useRef) lets the listener effect run exactly when Root mounts/unmounts,
  // instead of re-attaching on every unrelated `notification` cache write
  // (mark-as-read elsewhere, subscription updates) that returns a fresh ref.
  const [root, setRoot] = useState<HTMLElement | null>(null)
  const [isScrolled, setIsScrolled] = useState(false)

  const notification = data?.notification

  useEffect(() => {
    if (!root) return

    const handleScroll = throttle(() => {
      setIsScrolled(root.scrollTop > SCROLLED_THRESHOLD_PX)
    }, SCROLL_THROTTLE_MS)

    handleScroll()
    root.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      handleScroll.cancel()
      root.removeEventListener('scroll', handleScroll)
    }
  }, [root])

  if (loading) return null
  if (!notification) return null

  const config = getNotificationConfig(notification.__typename)

  return (
    <Root ref={setRoot}>
      {isDesktop && (
        <InboxPreviewActions
          notification={notification}
          scrolled={isScrolled}
        />
      )}

      <InboxContent>
        <InboxPreviewHeader
          icon={config.typeIcon}
          title={resolveNotificationTitle(config.title, notification)}
        />
        <VerticalDivider />
        <InboxPreviewBody notification={notification} />
        <VerticalDivider />
        <InboxPreviewReason reason={config.reason} />
      </InboxContent>
    </Root>
  )
}
